import clsx from "clsx";
import { motion } from "framer-motion";
import { Children, type ReactElement, cloneElement } from "react";

export function PlanLabelBadge(props: {
	children: React.ReactNode;
	variant?: "default" | "pro" | "believer" | "enterprise" | "partner";
	className?: string;
}) {
	const { children, variant = "default", className } = props;

	return (
		<div
			className={clsx(
				"text-sm font-bold tracking-widest inline-block uppercase",
				variant === "default" && "text-text-muted",
				variant === "pro" && "text-brand-primary",
				variant === "believer" && "text-yellow-500",
				variant === "enterprise" && "text-blue-500",
				variant === "partner" && "text-violet-500",
				className,
			)}
		>
			{children}
		</div>
	);
}

export function PlanPrice(props: {
	price?: string;
	fullPrice?: string;
	period: "monthly" | "annually" | "lifetime";
}) {
	const { price = "Free", fullPrice, period } = props;

	return (
		<div className="flex flex-col gap-0.5 pb-1 min-h-20 justify-start">
			<div className="flex flex-row flex-wrap items-baseline gap-1.5">
				<h2 className="text-3xl font-bold tracking-tight text-text-primary">
					{price}
				</h2>

				{fullPrice && (
					<p className="text-xs text-text-muted line-through decoration-border-default">
						{fullPrice}
					</p>
				)}
			</div>

			<div className="flex items-center gap-2">
				{price && (
					<span className="text-xs font-medium text-text-secondary">
						{period === "monthly"
							? "per month"
							: period === "annually"
								? "per year"
								: ""}
					</span>
				)}
			</div>
		</div>
	);
}

export function PlanDescription(props: { children: React.ReactNode }) {
	const { children } = props;

	return (
		<div className="text-[13px] text-text-secondary leading-normal mb-6">
			{children}
		</div>
	);
}

export function PlanCta(props: { children: React.ReactNode }) {
	const { children } = props;

	return <div className="mt-auto pt-4">{children}</div>;
}

type ElementWithClassName = ReactElement<{ className?: string }>;

export function PlanCtaButton(props: {
	children: ElementWithClassName;
	className?: string;
}) {
	const { children, className } = props;
	const child = Children.only(children) as ElementWithClassName;
	return cloneElement(child, {
		className: clsx(
			"rounded-xl h-10 bg-brand-primary hover:bg-brand-primary-hover text-text-inverse",
			"shadow-md shadow-[var(--ui-color-brand-primary)]/10 transition-all text-sm font-semibold",
			child.props.className,
			className,
		),
	});
}

export function PlanSecondaryButton(props: {
	children: ElementWithClassName;
	className?: string;
}) {
	const { children, className } = props;
	const child = Children.only(children) as ElementWithClassName;
	return cloneElement(child, {
		className: clsx(
			"rounded-xl h-10 text-sm border-border-subtle text-text-primary",
			"hover:bg-surface-subtle transition-all font-semibold",
			child.props.className,
			className,
		),
	});
}

export function PlanCard(props: {
	active?: boolean;
	comingSoon?: boolean;
	children: React.ReactNode;
}) {
	const { children, active = false, comingSoon = false } = props;

	return (
		<motion.div
			whileHover={
				comingSoon ? undefined : { y: -2, transition: { duration: 0.2 } }
			}
			className={clsx(
				"relative flex-1 rounded-2xl p-5 flex flex-col transition-all duration-300",
				"bg-surface-subtle border border-border-subtle",
				comingSoon
					? "shadow-sm opacity-80"
					: active
						? "ring-1 ring-brand-primary shadow-lg shadow-(--ui-color-brand-primary)/5"
						: "hover:border-border-default shadow-sm",
			)}
		>
			{active && (
				<div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-brand-primary text-text-inverse text-[9px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider shadow-md">
					Current
				</div>
			)}
			{children}
		</motion.div>
	);
}
